import React, { useRef, useState, useContext, Suspense } from 'react';
import classNames from 'classnames';
import { string, bool, shape } from 'prop-types';
import { Tooltip } from '@thd-olt-component-react/tooltip';
import { Alert } from '@thd-olt-component-react/alert';
import { Button } from '@thd-olt-component-react/core-ui';
import { extend, useDataModel } from '@thd-nucleus/data-sources';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { StoreLayoutOverlay } from './StoreLayoutOverlay';
import AisleBayTextMe from './AisleBayTextMe';
import { dataModel } from './dataModel';
import { publish, transformData, getLocalStore } from '../helper';

const AisleBaySingleItem = ({
  itemId, store, channel, centered, clsRemediation
}) => {
  const textMeTooltipRef = useRef(null);
  const storeId = store?.storeId;
  const storeName = store?.storeName;
  const isLocalized = store?.isLocalized;
  const isMobile = channel === 'mobile';
  const { placeholders, preservePlaceholders } = clsRemediation || {};
  const [storeOverlayOpen, setStoreOverlayOpen] = useState(false);
  const { isConsumerApp } = useContext(ExperienceContext);

  // request simple metadata for ssr
  const { data: metadata } = useDataModel('product', {
    variables: { itemId }
  });

  const { data: storeDetailsData } = useDataModel('storeDetails', {
    skip: !isConsumerApp,
    variables: { storeId }
  });

  const { availabilityType, identifiers, media } = metadata?.product || {};
  const { storeSkuNumber, productType = '', brandName, productLabel } = identifiers || {};
  const { type = '' } = availabilityType || {};
  const { url } = media?.image || {};
  const isOnlineOnly = /Online/i.test(type);
  const isAppliance = /MAJOR_APPLIANCE/i.test(productType);
  // in stores, not online-only
  const isCompatibleProductType = storeSkuNumber && (!isOnlineOnly || isAppliance);
  let zipCode = store?.storeZip;
  if (typeof window !== 'undefined') {
    const zipDetails = window?.THD_LOCALIZER_AUTO_INIT?.Localizer?.getDeliveryZipcodeDetails();
    zipCode = zipDetails?.zipcode;
  }
  // request fulfillment on client for inventory
  const { data: fulfillmentData, loading } = useDataModel('clientOnlyProduct', {
    variables: { itemId, storeId, zipCode },
    skip: !isCompatibleProductType || !isLocalized,
    ssr: false
  });

  const fulfillment = fulfillmentData?.product?.fulfillment;
  const { fulfillmentOptions } = fulfillment || {};
  const dataIsLocalized = Boolean(isLocalized && !loading && fulfillment);
  const isActiveSku = fulfillment?.anchorStoreStatus
    && /(active)|(in_season)/i.test(fulfillment?.anchorStoreStatusType);
  const localStore = getLocalStore(fulfillmentOptions);
  const isInStore = isActiveSku && (localStore?.inventory?.quantity || isAppliance);
  const {
    street, city, state, country, postalCode
  } = storeDetailsData?.storeDetails?.address || {};
  const { isLimitedQuantity, isInStock, quantity } = localStore?.inventory || {};

  // if in store, then get aisle bay info
  const { data: aisleBayData } = useDataModel('aislebay', {
    skip: !isInStore,
    ssr: false,
    variables: {
      storeId,
      storeSkuIds: storeSkuNumber ? [storeSkuNumber] : [],
    }
  });

  const aisleBayInfo = aisleBayData?.aislebay?.storeSkus?.[0]?.aisleBayInfo;
  const isAisleBayEmpty = aisleBayData && !aisleBayInfo;
  const { aisle, bay } = aisleBayInfo || {};

  const {
    hasAllRequiredProperties,
    aisleBayInventory,
    localStoreName,
    aisleBayMessage
  } = transformData(localStore, isAppliance, aisleBayInfo, storeName);

  // data invalidation
  if (!hasAllRequiredProperties) {
    // --> may cause cls from subsequent content insertion
    if (!placeholders) {
      return null;
    }

    // ('product') --> 0 CLS
    if (!isCompatibleProductType) {
      return null;
    }

    // --> may cause cls from collapse of early placeholders
    if (!preservePlaceholders) {
      // ('clientOnlyProduct')
      if (dataIsLocalized && !isInStore) return null;
      // ('aislebay')
      if (isAisleBayEmpty) return null;
    }
  }

  const aisleBayClasses = classNames('aisle-bay', {
    'aisle-bay--hidden': !hasAllRequiredProperties,
    'aisle-bay--centered': centered
  });

  const AisleBayInventory = () => (
    <span className="aisle-bay__message">
      <Alert type="success" inline>
        {aisleBayInventory}&ensp;
      </Alert>
    </span>
  );

  const onStoreLayoutClick = () => {
    if (isConsumerApp) {
      window.LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('NATIVE_APP.STORE_MAP', {
        storeId,
        storeName,
        street,
        city,
        state,
        country,
        postalCode,
        sku: storeSkuNumber,
        aisle,
        bay,
        friendlyAisleDescription: aisleBayInfo?.invLocDescFriendly,
        imageURL: url,
        quantity,
        storePhone: localStore?.storePhone,
        isLimitedStock: isLimitedQuantity,
        brandName,
        productLabel,
        stockStatus: isInStock
      });
    } else setStoreOverlayOpen(true);
  };

  function handleTextMe(event) {
    event.preventDefault();
    event.stopPropagation();
    publish('open');
    if (textMeTooltipRef?.current) {
      textMeTooltipRef.current.resetState();
    }
  }

  return (
    <fieldset className={aisleBayClasses} data-component="AisleBaySingleItem">
      <legend className="aisle-bay__legend">
        <span className="aisle-bay__message u__bold">
          <>{localStoreName} Store</>

        </span>
        {(isMobile) && <AisleBayInventory />}
      </legend>
      <section className="aisle-bay__message">
        {(!isMobile) && <AisleBayInventory />}
        {!isAppliance && (
          <>
            <span className="aisle-bay__message">{aisleBayMessage}&ensp;</span>
            <Tooltip
              channel={channel}
              closeButton
              placement="bottom"
              content={(
                <AisleBayTextMe
                  itemId={itemId}
                  storeId={storeId}
                  aisleBayText={aisleBayMessage}
                  ref={textMeTooltipRef}
                />
              )}
            >
              {/* eslint-disable-next-line */}
              <a className="u__default-link aisle-bay__message" onClick={handleTextMe}>
                Text to Me
              </a>
            </Tooltip>
            {isMobile && (
              <span className="aisle-bay__storelayout">
                <Button onClick={onStoreLayoutClick} link>Store Map</Button>
                {storeOverlayOpen && (
                  <StoreLayoutOverlay
                    onLayoutClick={setStoreOverlayOpen}
                    storeId={storeId}
                    storeName={storeName}
                    aislebay={aisleBayInfo}
                  />
                )}
              </span>
            )}
          </>
        )}
      </section>
    </fieldset>
  );
};

AisleBaySingleItem.displayName = 'AisleBaySingleItem';

AisleBaySingleItem.propTypes = {
  itemId: string,
  store: shape({
    storeId: string,
    storeName: string,
  }).isRequired,
  channel: string.isRequired,
  // justifies and aligns all text centered
  centered: bool,
  clsRemediation: shape({
    // reserves whitespace while loading
    placeholders: bool,
    // disallows collapse of empty whitespace
    preservePlaceholders: bool,
  }).isRequired
};

AisleBaySingleItem.defaultProps = {
  itemId: null,
  centered: false
};

AisleBaySingleItem.dataModel = extend({}, dataModel);

export { AisleBaySingleItem };